import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Card,
  CardContent,
  Typography,
  Button,
  Checkbox,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Close as CloseIcon,
  SelectAll as SelectAllIcon,
  Send as SendIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import Whatshot from '@mui/icons-material/Whatshot';

import {getAttendees} from '../../service/OFI/getAttendees.js';
import { useNavigate } from 'react-router-dom';

const AttendeeList = ({ selectedOpenhome }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [attendees, setAttendees] = useState([]);
  const [filteredAttendees, setFilteredAttendees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedAttendees, setSelectedAttendees] = useState(new Set());
  const [error, setError] = useState(null);
  const [sendingData, setSendingData] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const navigate = useNavigate();
  
  const fetchAttendees = async () => {
    try {
      setError(null);
      setIsLoading(true);      
      const attendeeData = await getAttendees(selectedOpenhome?.propertyopentimeid);
      setAttendees(attendeeData.data);
      setFilteredAttendees(attendeeData.data);
      setIsLoading(false);
    } catch (error) {
     
      setError('Failed to fetch attendees. Please try again.');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendees();
  }, []);

  
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (query === '') {
      setFilteredAttendees(attendees);
    } else {
      const filtered = attendees.filter(item => {
        const searchTerm = query.toLowerCase();
        const isFirstname = item.firstname?.toLowerCase().includes(searchTerm);
        const isLastName = item.lastname?.toLowerCase().includes(searchTerm);
        const isMobilePhone = item.mobilephone?.toString().includes(searchTerm);
        const isEmail = item.email?.toString().includes(searchTerm);
        const isFullName = `${item.firstname || ''} ${item.lastname || ''}`.toLowerCase().includes(searchTerm);
        return isFirstname || isLastName || isFullName || isMobilePhone || isEmail;
      });
      setFilteredAttendees(filtered);
    }
  };

  const handleEdit = (item) => {
       navigate('/ofi/ofiapp', { 
      state: { 
      selectedOpenhome: selectedOpenhome, 
      attendeeDetails: item,
      activeTab: 0 ,
      isEditing: true 
        } 
      });
    };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (selectionMode) {
      setSelectedAttendees(new Set());
    }
  };

  const toggleAttendeeSelection = (attendeeId) => {
    const newSelected = new Set(selectedAttendees);
    if (newSelected.has(attendeeId)) {
      newSelected.delete(attendeeId);
    } else {
      newSelected.add(attendeeId);
    }
    setSelectedAttendees(newSelected);
  };

  const selectAllAttendees = () => {
    const allIds = filteredAttendees.map(attendee => attendee.email || attendee.mobilephone);
    setSelectedAttendees(new Set(allIds));
  };

  const deselectAllAttendees = () => {
    setSelectedAttendees(new Set());
  };

  const handleSendClick = () => {
    setConfirmDialog(true);
  };

  const sendSelectedAttendees = async () => {
    setConfirmDialog(false);
    setSendingData(true);
    
    try {
  
      const selectedAttendeesData = filteredAttendees.filter(
        attendee => selectedAttendees.has(attendee.email || attendee.mobilephone)
      );
      
   
      const response = await fetch('/api/send-attendees', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attendees: selectedAttendeesData,
          timestamp: new Date().toISOString()
        })
      });

  
      setSendSuccess(true);
      
      // Exit selection mode after sending
      setSelectionMode(false);
      setSelectedAttendees(new Set());
      
      // Hide success message after 3 seconds
      setTimeout(() => setSendSuccess(false), 3000);
      
    } catch (error) {
      setError('Failed to send attendees. Please try again.');
    } finally {
      setSendingData(false);
    }
  };

  const renderInterestLevel = (item) => {
    if (!item.interested) {
      return <Typography variant="body2" color="text.secondary">No interest level provided</Typography>;
    }

    const level = Number(item.interestlevel?.trim() || 0);
    const fires = [];
    
    for (let i = 0; i < 3; i++) {
      let color = '#ccc';
      if (i < level) {
        color = i === 0 ? '#ff9800' : i === 1 ? '#ff5722' : '#f44336';
      }
      fires.push(
        <Whatshot key={i} sx={{ color, fontSize: 20 }} />
      );
    }
    
    return <Box sx={{ display: 'flex', alignItems: 'center' }}>{fires}</Box>;
  };

  const renderAttendeeCard = (item, index) => {
    const attendeeId = item.feedbackId || item.id || item.email || item.mobilephone || `attendee-${index}`;
    const isSelected = selectedAttendees.has(attendeeId);
    const fullName = item.firstname && item.lastname 
      ? `${item.firstname} ${item.lastname}` 
      : `${item.firstName || ''} ${item.lastName || ''}`;

    return (
      <Card 
        sx={{ 
          mb: 2, 
          position: 'relative',
          border: selectionMode && isSelected ? '2px solid #1976d2' : 'none',
          backgroundColor: selectionMode && isSelected ? '#f3f7ff' : 'white'
        }}
      >
        <CardContent>
          {selectionMode && (
            <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
              <Checkbox
                checked={isSelected}
                onChange={() => toggleAttendeeSelection(attendeeId)}
                color="primary"
              />
            </Box>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="h2" sx={{ ml: selectionMode ? 5 : 0 }}>
              {fullName}
            </Typography>
            {!selectionMode && (
              <IconButton onClick={() => handleEdit(item)} color="primary">
                <EditIcon />
              </IconButton>
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {item.email || "No email provided"}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {(item.mobilephone?.trim()) || (item.mobilePhone?.trim()) || "No mobile phone provided"}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {item.feedback || "No feedback provided"}
          </Typography>

          {(item.priceFeedbackFrom || item.priceFeedbackTo) && (
            <Box sx={{ mb: 1 }}>
              {item.priceFeedbackFrom && item.priceFeedbackTo && (
                <Typography variant="body2" color="text.secondary">
                  Price From: ${parseInt(item.priceFeedbackFrom).toLocaleString()} - Price To: ${parseInt(item.priceFeedbackTo).toLocaleString()}
                </Typography>
              )}
              {item.priceFeedbackFrom && !item.priceFeedbackTo && (
                <Typography variant="body2" color="text.secondary">
                  Price From: ${parseInt(item.priceFeedbackFrom).toLocaleString()}
                </Typography>
              )}
              {item.priceFeedbackTo && !item.priceFeedbackFrom && (
                <Typography variant="body2" color="text.secondary">
                  Price To: ${parseInt(item.priceFeedbackTo).toLocaleString()}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            {renderInterestLevel(item)}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderSkeleton = () => (
    <Box>
      {[...Array(5)].map((_, index) => (
        <Card key={index} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ width: '60%', height: 24, bgcolor: 'grey.300', borderRadius: 1 }} />
              <Box sx={{ width: 24, height: 24, bgcolor: 'grey.300', borderRadius: 1 }} />
            </Box>
            <Box sx={{ width: '80%', height: 16, bgcolor: 'grey.200', borderRadius: 1, mb: 1 }} />
            <Box sx={{ width: '40%', height: 16, bgcolor: 'grey.200', borderRadius: 1, mb: 1 }} />
            <Box sx={{ width: '90%', height: 16, bgcolor: 'grey.200', borderRadius: 1, mb: 1 }} />
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Header with selection toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Button
            variant={selectionMode ? "contained" : "outlined"}
            startIcon={selectionMode ? <CloseIcon /> : <SelectAllIcon />}
            onClick={toggleSelectionMode}
            sx={{ ml: 1 }}
          >
            {selectionMode ? 'Cancel' : 'Select'}
          </Button>
        </Box>
      </Box>

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search Attendees"
        value={searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        sx={{ mb: 2 }}
        slotProps={{
          input: {
            endAdornment: searchQuery && (
              <IconButton onClick={() => handleSearch('')} edge="end">
                <CloseIcon />
              </IconButton>
            )
          }
        }}
      />

      {/* Selection controls */}
      {selectionMode && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`${selectedAttendees.size} selected`} 
            color="primary" 
            variant="outlined"
          />
          <Button size="small" onClick={selectAllAttendees}>
            Select All
          </Button>
          <Button size="small" onClick={deselectAllAttendees}>
            Deselect All
          </Button>
          <Button 
            variant="contained" 
            startIcon={sendingData ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleSendClick}
            disabled={selectedAttendees.size === 0 || sendingData}
            sx={{ 
              minWidth: 150,
              opacity: selectedAttendees.size === 0 ? 0.6 : 1
            }}
          >
            {sendingData ? 'Sending...' : 'Send Selected'}
          </Button>
        </Box>
      )}

      {/* Success message */}
      {sendSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Successfully sent {selectedAttendees.size} attendee(s) to the API!
        </Alert>
      )}

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Content */}
      {isLoading || refreshing ? (
        renderSkeleton()
      ) : Array.isArray(filteredAttendees) && filteredAttendees.length > 0 ? (
        <Box>
          {filteredAttendees.map((item, index) => {
            const uniqueKey = item.feedbackId || item.id || item.email || item.mobilephone || `attendee-${index}`;
            return (
              <React.Fragment key={uniqueKey}>
                {renderAttendeeCard(item, index)}
              </React.Fragment>
            );
          })}
        </Box>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center">
              No Attendees Found
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Send Selected Attendees
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to send {selectedAttendees.size} selected attendee(s) to the API?
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              This action will send the following data for each selected attendee:
            </Typography>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <Typography component="li" variant="body2">Name and contact information</Typography>
              <Typography component="li" variant="body2">Feedback and interest level</Typography>
              <Typography component="li" variant="body2">Price feedback range</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={sendSelectedAttendees} 
            variant="contained"
            disabled={sendingData}
          >
            Send Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendeeList;